import React, { useState } from 'react';
import { Icon } from '../ui';
import { EnvBadge, ThemeToggle } from './Topbar';

/**
 * Frame for the signed-out screens (sign in, MFA, password reset): a brand
 * panel on the left, the form on the right, collapsing to the form alone on a
 * phone. The environment badge is here too — signing into PROD by mistake is
 * exactly the error it exists to prevent.
 */
export default function AuthLayout({ title, subtitle, steps, current, children }) {
  return (
    <div className="auth-page">
      <aside className="auth-brand" aria-hidden="true">
        <div className="auth-brand-inner">
          <span className="ross-wordmark ross-wordmark--login">
            <span className="prefix">&gt;&nbsp;</span><span className="letters">ROSS</span><span className="underscore">_</span>
          </span>
          <p className="auth-brand-title">Multitenant admin console</p>
          <p className="auth-brand-text">
            Users, agents, channels and models for the Ross platform, in one place.
          </p>
          <ul className="auth-brand-points">
            <li><Icon name="shield" /> Cognito sign-in with authenticator MFA</li>
            <li><Icon name="activity" /> Live agent state and usage</li>
            <li><Icon name="sliders" /> Tiers, tools and channel policy</li>
          </ul>
        </div>
      </aside>
      <main className="auth-main">
        <div className="auth-corner">
          <EnvBadge />
          <ThemeToggle />
        </div>
        <div className="auth-card">
          <span className="ross-wordmark ross-wordmark--sidebar auth-card-mark" aria-label="Ross">
            <span className="prefix">&gt;&nbsp;</span><span className="letters">ROSS</span><span className="underscore">_</span>
          </span>
          {steps && (
            <ol className="auth-steps" aria-label="Progress">
              {steps.map((s, i) => (
                <li key={s} className={i < current ? 'done' : i === current ? 'current' : ''}
                    aria-current={i === current ? 'step' : undefined}>
                  <span className="auth-step-dot">{i < current ? <Icon name="check" size={12} strokeWidth={2.5} /> : i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
          )}
          {title && <h2 className="auth-title">{title}</h2>}
          {subtitle && <p className="auth-subtitle">{subtitle}</p>}
          {children}
        </div>
      </main>
    </div>
  );
}

// Password input with a show/hide toggle. `autoComplete` matters here: it is
// what lets a password manager fill and save the right field.
export function PasswordInput({ id, value, onChange, autoComplete = 'current-password', autoFocus, minLength, required = true }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="input-affix password-input">
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        className="form-input"
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        minLength={minLength}
        required={required}
        spellCheck={false}
      />
      <button type="button" className="btn btn-ghost btn-icon btn-sm affix-end"
              onClick={() => setShown((s) => !s)} aria-label={shown ? 'Hide password' : 'Show password'}
              aria-pressed={shown}>
        <Icon name={shown ? 'eyeOff' : 'eye'} />
      </button>
    </div>
  );
}

// One field for a 6-digit code, big and spaced so it can be checked at a glance
// against the authenticator. Digits only; paste of "123 456" is normalised.
export function CodeInput({ id, value, onChange, autoFocus = true }) {
  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      pattern="[0-9]{6}"
      autoComplete="one-time-code"
      className="form-input code-input"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      autoFocus={autoFocus}
      required
    />
  );
}
